const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

class AdminApiService {
  async request(endpoint, options = {}) {
    const url = `${API_URL}${endpoint}`;

    const config = {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Don't set Content-Type for FormData
    if (options.body instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    const response = await fetch(url, config);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // ==================== Authentication ====================

  async googleAuth(credential) {
    return this.request('/admin/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    });
  }

  async logout() {
    return this.request('/admin/auth/logout', {
      method: 'POST',
    });
  }

  async getMe() {
    return this.request('/admin/auth/me');
  }

  // ==================== Dashboard Stats ====================

  async getStats() {
    return this.request('/admin/stats');
  }

  async getUsersCount() {
    return this.request('/admin/stats/users');
  }

  async getAgentsCount() {
    return this.request('/admin/stats/agents');
  }

  // ==================== Users ====================

  async getUsers(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);
    if (params.sort) queryParams.append('sort', params.sort);

    const query = queryParams.toString();
    return this.request(`/admin/users${query ? `?${query}` : ''}`);
  }

  async getUser(userId) {
    return this.request(`/admin/users/${userId}`);
  }

  async updateUserStatus(userId, status) {
    return this.request(`/admin/users/${userId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async exportUsers(format = 'csv') {
    return this.request(`/admin/users/export?format=${format}`);
  }

  // ==================== Agents ====================

  async getAgents(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);

    const query = queryParams.toString();
    return this.request(`/admin/agents${query ? `?${query}` : ''}`);
  }

  async getAgent(agentId) {
    return this.request(`/admin/agents/${agentId}`);
  }

  async exportAgents(format = 'csv') {
    return this.request(`/admin/agents/export?format=${format}`);
  }

  async getAgentConversations(agentId, params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    const query = queryParams.toString();
    return this.request(`/admin/agents/${agentId}/conversations${query ? `?${query}` : ''}`);
  }

  async getAgentLeads(agentId, params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    const query = queryParams.toString();
    return this.request(`/admin/agents/${agentId}/leads${query ? `?${query}` : ''}`);
  }

  async getAgentAppointments(agentId, params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    const query = queryParams.toString();
    return this.request(`/admin/agents/${agentId}/appointments${query ? `?${query}` : ''}`);
  }

  async getAgentEmails(agentId, params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    const query = queryParams.toString();
    return this.request(`/admin/agents/${agentId}/emails${query ? `?${query}` : ''}`);
  }

  // ==================== Conversations ====================

  async getConversations(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.agentId) queryParams.append('agent_id', params.agentId);

    const query = queryParams.toString();
    return this.request(`/admin/conversations${query ? `?${query}` : ''}`);
  }

  async getConversation(conversationId) {
    return this.request(`/admin/conversations/${conversationId}`);
  }

  // ==================== Custom Requests ====================

  async getCustomRequests(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.status) queryParams.append('status', params.status);

    const query = queryParams.toString();
    return this.request(`/admin/requests${query ? `?${query}` : ''}`);
  }

  async getCustomRequest(requestId) {
    return this.request(`/admin/requests/${requestId}`);
  }

  async updateCustomRequest(requestId, data) {
    return this.request(`/admin/requests/${requestId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async exportCustomRequests(format = 'csv') {
    return this.request(`/admin/requests/export?format=${format}`);
  }

  // ==================== Feedback ====================

  async getFeedback(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.type) queryParams.append('type', params.type);
    if (params.status) queryParams.append('status', params.status);

    const query = queryParams.toString();
    return this.request(`/admin/feedback${query ? `?${query}` : ''}`);
  }

  async getFeedbackItem(feedbackId) {
    return this.request(`/admin/feedback/${feedbackId}`);
  }

  async updateFeedback(feedbackId, data) {
    return this.request(`/admin/feedback/${feedbackId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async exportFeedback(format = 'csv') {
    return this.request(`/admin/feedback/export?format=${format}`);
  }

  // ==================== Documentation ====================

  async getDocs() {
    return this.request('/admin/docs');
  }

  async getDoc(slug) {
    return this.request(`/admin/docs/${slug}`);
  }

  async updateDoc(slug, data) {
    return this.request(`/admin/docs/${slug}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async createDoc(data) {
    return this.request('/admin/docs', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteDoc(slug) {
    return this.request(`/admin/docs/${slug}`, {
      method: 'DELETE',
    });
  }

  async uploadDocImage(file) {
    const formData = new FormData();
    formData.append('image', file);

    return this.request('/admin/docs/images', {
      method: 'POST',
      body: formData,
    });
  }

  async deleteDocImage(imageId) {
    return this.request(`/admin/docs/images/${imageId}`, {
      method: 'DELETE',
    });
  }

  // Documentation Structure (sections/articles format)
  async getDocsStructure() {
    return this.request('/admin/docs/structure');
  }

  async saveDocsStructure(sections) {
    return this.request('/admin/docs/structure', {
      method: 'PUT',
      body: JSON.stringify({ sections }),
    });
  }

  async updateDocArticle(sectionId, articleId, data) {
    return this.request(`/admin/docs/structure/${sectionId}/${articleId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async uploadDocScreenshot(sectionId, articleId, file, sectionTitle = 'New Section') {
    const formData = new FormData();
    formData.append('screenshot', file);
    formData.append('sectionId', sectionId);
    formData.append('articleId', articleId);
    formData.append('sectionTitle', sectionTitle);

    return this.request('/admin/docs/screenshot', {
      method: 'POST',
      body: formData,
    });
  }

  // ==================== Admin Users ====================

  async getAdminUsers() {
    return this.request('/admin/admins');
  }

  // ==================== Settings ====================

  async getSettings() {
    return this.request('/admin/settings');
  }

  async updateSettings(data) {
    return this.request('/admin/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ==================== Email Configs ====================

  async createEmailConfig(config) {
    return this.request('/admin/settings/email', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  }

  async updateEmailConfig(configId, config) {
    return this.request(`/admin/settings/email/${configId}`, {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  async deleteEmailConfig(configId) {
    return this.request(`/admin/settings/email/${configId}`, {
      method: 'DELETE',
    });
  }

  async sendEmail(data) {
    return this.request('/admin/email/send', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async sendBulkEmail(data) {
    return this.sendEmail(data);
  }

  // ==================== AI Formatting ====================

  async formatWithAI(text, field) {
    return this.request('/admin/ai/format', {
      method: 'POST',
      body: JSON.stringify({ text, field }),
    });
  }

  // ==================== Analytics ====================

  async getAnalytics(params = {}) {
    const range = params.range || '30d';
    return this.request(`/admin/analytics?range=${range}`);
  }

  async exportAnalytics(params = {}) {
    const { range = '30d', format = 'csv' } = params;
    const response = await fetch(`${API_URL}/admin/analytics/export?range=${range}&format=${format}`, {
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  }

  async getUserAnalytics(range = '30d') {
    return this.request(`/admin/analytics/users?range=${range}`);
  }

  async getAgentAnalytics(range = '30d') {
    return this.request(`/admin/analytics/agents?range=${range}`);
  }

  async getConversationAnalytics(range = '30d') {
    return this.request(`/admin/analytics/conversations?range=${range}`);
  }
}

export const adminApi = new AdminApiService();
export default adminApi;
